/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { RatingComponent } from '@msdyn365-commerce/components';
import { IAny, IComponent, IComponentProps, ICoreContext, IGeneric, ITelemetry, msdyn365Commerce } from '@msdyn365-commerce/core';
import { getPayloadObject, getTelemetryAttributes, IPayLoad, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

/**
 * IRefineRatingItemComponent Interface.
 */
export interface IRefineRatingItemComponent extends IComponent<IRefineRatingProps> {}

/**
 * Refine rating item props Interface.
 */
export interface IRefineRatingProps extends IComponentProps<{}> {
    context: ICoreContext<IGeneric<IAny>>;
    avgRatingCount: number;
    ratingCountText?: string;
    className?: string;
    ratingAriaLabel?: string;
    telemetry?: ITelemetry;
    refineItemUrl?: string;
    refineRatingName?: string;
    productCount?: string;
    telemetryContent?: ITelemetryContent;
    isMobileView?: boolean;

    onClick(event: React.MouseEvent<HTMLAnchorElement | HTMLInputElement>, avgRatingCount?: number, ratingCountText?: string): void;
}

/**
 * Refine Rating item.
 * @param props - IRefineRatingProps.
 * @returns - Render rating item.
 */
const RefineRatingFunctionalComponent: React.FC<IRefineRatingProps> = (props: IRefineRatingProps) => {
    const {
        refineItemUrl,
        avgRatingCount,
        ratingCountText,
        ratingAriaLabel,
        refineRatingName,
        context,
        productCount,
        isMobileView,
        id,
        typeName,
        className,
        onClick
    } = props;

    const payLoad: IPayLoad = getPayloadObject('click', props.telemetryContent!, '');

    /**
     * OnClickHandler Method.
     * @param event - Mouse event.
     */
    const onClickHandler = React.useCallback((event: React.MouseEvent<HTMLAnchorElement | HTMLInputElement>) => {
        event.preventDefault();
        onClick(event, avgRatingCount, ratingCountText);
    }, []);
    payLoad.contentAction.etext = refineRatingName;
    const attribute = getTelemetryAttributes(props.telemetryContent!, payLoad);

    return (
        <a
            href={refineItemUrl}
            aria-label={ratingAriaLabel ?? 'Rating'}
            role='option'
            onClick={onClickHandler}
            data-testid='testid-refine-rating-item-container'
            {...attribute}
        >
            <RatingComponent
                className={className}
                avgRating={avgRatingCount}
                ratingCount={ratingCountText ?? `${avgRatingCount}`}
                readOnly
                context={context}
                ariaLabel=''
                id={id}
                typeName={typeName}
                data={{}}
            />
            <span className='refine-submenu-item__rating' aria-hidden={isMobileView ? 'true' : undefined}>
                {`(${productCount})`}
            </span>
        </a>
    );
};

/**
 * Refine rating item Component.
 */
export const RefineRatingItemComponent: React.FunctionComponent<IRefineRatingProps> = msdyn365Commerce.createComponent<
    // @ts-expect-error -- Required for this signature.
    IRefineRatingItemComponent
>('Refine-Rating-Item', { component: React.memo(RefineRatingFunctionalComponent) });
